import { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { themeColors, themeIcon } from "../../../constants/constants";
import { useSelector } from "react-redux";
import { AppDispatch, RootState, useAppDispatch } from "../../../store";
import useMobileCheck from "../../../hooks/useMobileChecker";
import api from "../../../services/api";
import KatakomButton from "../../shared/KatakomButton";
import KatakomTable from "../../shared/KatakomTable";
import { ButtonGroup, Row } from "../style";
import { addDocumentDopuna, addTempDocumentDopuna, deleteTempDocumentDopuna, resetState } from "../../../store/CaseDopunaReducer";
import KatakomModal from "../../shared/KatakomModal";
import { SuccessMessageModal } from "../../customComponent/SuccessMessageModal";
import { WaitingModal } from "../../customComponent/WaitingModal";
import { ErrorMessageModal } from "../../customComponent/ErrorMessageModal";
import { useNavigate } from "react-router-dom";

interface Props {
    closeModal: () => void;
    caseData: any;
}

export const DopunaMgsiModal: FC<Props> = ({ caseData, closeModal }) => {
    const isMobile = useMobileCheck();
    const tempDokumenta = useSelector((state: RootState) => state.caseDopuna.tempDokumenta);
    const slucajDokumenta = useSelector((state: RootState) => state.caseDopuna.dokumenta);

    const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
    const [waitingModalOpen, setWaitingModalOpen] = useState<boolean>(false);
    const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);

    const dispatch: AppDispatch = useAppDispatch();

    let navigate = useNavigate();

    const routeChange = (path: string) => {
        navigate(path);
    };

    const handleDeleteDocument = (dokument: any) => {
        dispatch(deleteTempDocumentDopuna({ vrstaIspraveId: dokument.vrstaIspraveId || 'mgsi', name: dokument.name }));
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (selectedFiles) {
            const newFiles = Array.from(selectedFiles).filter((file) => file.type === "application/pdf");
            await Promise.all(
                newFiles.map((file) => {
                    return new Promise<string>(async (resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = async () => {
                            const base64String = reader.result?.toString().split(",")[1];
                            if (base64String) {
                                try {
                                    const isSigned = await hasDigitalSignature(base64String);
                                    dispatch(
                                        addTempDocumentDopuna({
                                            name: file.name,
                                            file: base64String,
                                            isSigned: Boolean(isSigned),
                                            category: "Kategorija 1",
                                            vrstaIspraveId: "mgsi", // Dodajemo placeholder za MGSI fajlove
                                        })
                                    );
                                    resolve(base64String);
                                } catch (error) {
                                    reject(error);
                                }
                            } else {
                                reject(new Error("Error while reading file"));
                            }
                        };
                        reader.onerror = () => {
                            reject(new Error("Error while reading file"));
                        };
                    });
                })
            );
        }
    };

    const submitFiles = async () => {
        setWaitingModalOpen(true);
        const documents = [];
        try {
            for (const dok of tempDokumenta) {
                const response = await api.post(`api/v1/posalji-fajl`, {
                    base64Document: dok.file,
                });
                // Za MGSI endpoint ne treba vrstaIspraveId, samo id_prijem_fajl_esalter
                documents.push({ id_prijem_fajl_esalter: response?.data.id_prijem_fajl_esalter });
            }

            dispatch(addDocumentDopuna(documents));
        } catch (e) {
            setErrorModalOpen(true);
        } finally {
            setWaitingModalOpen(false);
        }
    };

    const handleFinish = async () => {
        if (slucajDokumenta.length > 0) {
            try {
                // Koristimo novi endpoint za MGSI
                const res = await api.post(`api/v1/send-additional-data-from-mgsi?idPrijemEsalter=${caseData.id_prijem_esalter}`, {
                    dokumenta: slucajDokumenta,
                });

                if (res.status !== 200) {
                    setErrorModalOpen(true);
                } else {
                    setSuccessModalOpen(true);
                }
            } catch (e) {
                setErrorModalOpen(true);
            }
        }
    };

    useEffect(() => {
        if (slucajDokumenta.length > 0) {
            handleFinish();
        }
    }, [slucajDokumenta]);



    const hasDigitalSignature = async (base64EncodedFile: string) => {
        const response = await api.post(`api/v1/has-digital-signature`, {
            base64Document: base64EncodedFile,
        });

        return response?.data;
    };

    const columns = [
        { label: "Ид", value: "id" },
        { label: "Документ", value: "name" },
        { label: "Категорија документа", value: "category" },
        { label: "Електронски потпис", value: "isSigned" },
        { label: "", value: "delete" },
    ];

    const areAllFilesSigned = () => {
        return tempDokumenta.length > 0 && tempDokumenta.every((doc) => doc.isSigned);
    };



    return (
        <>
            <KatakomModal isOpen={successModalOpen} setIsOpen={setSuccessModalOpen}>
                <SuccessMessageModal
                    closeModal={() => {
                        routeChange("/pocetna");
                        dispatch(resetState());
                        setSuccessModalOpen(false);
                        closeModal();
                    }}
                    message="Успешно сте послали допуну/жалбу!"
                />
            </KatakomModal>

            <KatakomModal isOpen={waitingModalOpen} setIsOpen={setWaitingModalOpen}>
                <WaitingModal message="Захтев се шаље молимо вас сачекајте!" />
            </KatakomModal>

            <KatakomModal isOpen={errorModalOpen} setIsOpen={setErrorModalOpen}>
                <ErrorMessageModal
                    closeModal={() => {
                        routeChange("/pocetna");
                        dispatch(resetState());
                        setErrorModalOpen(false);
                        closeModal();
                    }}
                    message="Дошло је до грешке приликом слања захтева!"
                />
            </KatakomModal>
            <Container>
                {tempDokumenta.length !== 0 ? (
                    <div style={{ margin: "15px" }}>
                        <KatakomTable columns={columns} data={tempDokumenta} onDelete={handleDeleteDocument} />
                    </div>
                ) : null}

                <Row style={{ display: "flex", justifyContent: "center" }}>
                    <Label>
                        Додај фајл
                        <Input type="file" onChange={handleFileUpload} accept=".pdf" multiple />
                    </Label>
                </Row>
                <ButtonGroup>
                    <KatakomButton
                        label={"Одустани"}
                        theme="secondary"
                        onClick={() => {
                            closeModal();
                        }}
                    />
                    <KatakomButton
                        label={"Додај"}
                        disabled={tempDokumenta.length > 0 && !areAllFilesSigned()}
                        onClick={() => {
                            if (tempDokumenta.length > 0) {
                                submitFiles();
                            } else {
                                handleFinish();
                            }
                        }}
                    />
                </ButtonGroup>
            </Container>
        </>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 10px;
`;

const Label = styled.label`
    font-size: 16px;
    font-weight: 500;
    display: flex;
    height: 50px;
    align-items: center;
    justify-content: center;
    margin: 30px 0 0 0;
    width: 200px;
    background-color: ${themeColors.addDocuentLabelBackgroundColor};
    color: white;
    box-shadow: 5px 5px 10px 0px #00000040;
    border-radius: 4px;
    cursor: pointer;
    transition: border 0.3s;

    input[type="file"] {
        display: none;
    }
`;

const Input = styled.input``;
